import { useState } from "react";
import { projects, projectCategories } from "@/data/projects";
import PageHeader from "@/components/PageHeader";
import ProjectCard from "@/components/ProjectCard";
import ContactCta from "@/components/ContactCta";
import { Facebook, Instagram } from "lucide-react";
import { site } from "@/data/site";

export default function Projekter() {
    const [active, setActive] = useState("alle");
    const filtered =
        active === "alle"
            ? projects
            : projects.filter((p) => p.category === active);

    return (
        <div data-testid="page-projekter">
            <PageHeader
                overline="Projekter"
                title="Vores arbejde — projekt for projekt."
                subtitle="Et udvalg af tidligere opgaver fra Silkeborg og omegn. Galleriet opdateres løbende med nye projekter — hver med egne billeder, tekst og link til Facebook."
            />

            <section className="py-12 sm:py-16 bg-[var(--ns-bg)]">
                <div className="container-ns">
                    <div
                        data-testid="project-filters"
                        className="flex flex-wrap gap-2 sm:gap-3 mb-10"
                    >
                        {projectCategories.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`filter-${c.id}`}
                                onClick={() => setActive(c.id)}
                                className={`px-4 sm:px-5 py-2.5 rounded-full text-sm font-display font-bold tracking-wide transition-all border ${
                                    active === c.id
                                        ? "bg-[var(--ns-blue)] text-white border-[var(--ns-blue)] shadow-md"
                                        : "bg-white text-[var(--ns-text)] border-[var(--ns-border)] hover:border-[var(--ns-blue)] hover:text-[var(--ns-blue)]"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="py-20 text-center">
                            <p className="text-[var(--ns-muted)]">
                                Ingen projekter i denne kategori endnu.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                            {filtered.map((p, i) => (
                                <ProjectCard key={p.id} project={p} index={i} />
                            ))}
                        </div>
                    )}

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="p-8 sm:p-10 rounded-md bg-white border border-[var(--ns-border)] flex items-center gap-5 sm:gap-6">
                            <div className="w-14 h-14 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center flex-shrink-0">
                                <Facebook className="w-7 h-7" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-display font-extrabold text-lg tracking-tight mb-1">
                                    Følg på Facebook
                                </h3>
                                <p className="text-[var(--ns-muted)] text-sm leading-relaxed mb-3">
                                    Flere billeder og fulde historier fra
                                    hvert projekt.
                                </p>
                                <a
                                    href={site.facebookUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="projekter-facebook-link"
                                    className="inline-flex items-center gap-2 text-sm font-display font-bold text-[var(--ns-blue)] hover:gap-3 transition-all"
                                >
                                    Gå til Facebook →
                                </a>
                            </div>
                        </div>

                        <div className="p-8 sm:p-10 rounded-md bg-white border border-[var(--ns-border)] flex items-center gap-5 sm:gap-6">
                            <div className="w-14 h-14 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center flex-shrink-0">
                                <Instagram className="w-7 h-7" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-display font-extrabold text-lg tracking-tight mb-1">
                                    Følg på Instagram
                                </h3>
                                <p className="text-[var(--ns-muted)] text-sm leading-relaxed mb-3">
                                    Korte videoer og billeder fra
                                    byggepladsen i real-time.
                                </p>
                                <a
                                    href={site.instagramUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="projekter-instagram-link"
                                    className="inline-flex items-center gap-2 text-sm font-display font-bold text-[var(--ns-blue)] hover:gap-3 transition-all"
                                >
                                    Gå til Instagram →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactCta
                title="Har I et projekt i tankerne?"
                subtitle="Fortæl mig om det — jeg kommer gerne ud og ser på det og giver et uforpligtende tilbud."
            />
        </div>
    );
}
